import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "lib/store/useAppStore";

export default function GlobalAlert() {
  const alert = useAppStore((s) => s.alert);
  const closeAlert = useAppStore((s) => s.closeAlert);
  const { t, i18n } = useTranslation();

  const durationMs = useMemo(() => alert.duration ?? 3000, [alert.duration]);
  const [remainSec, setRemainSec] = useState(Math.ceil(durationMs / 1000));

  useEffect(() => {
    if (!alert.open) return;
    setRemainSec(Math.ceil(durationMs / 1000));

    if (!alert.autoClose) return;

    const timeoutId = setTimeout(() => {
      closeAlert?.();
    }, durationMs);

    const intervalId = setInterval(() => {
      setRemainSec((prev) => (prev > 1 ? prev - 1 : 1));
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [alert.open, alert.autoClose, durationMs, closeAlert]);

  const alertText = useMemo(() => {
    if (alert.textKey) {
      return t(alert.textKey, {
        ...(alert.textParams || {}),
        defaultValue: alert.text || "",
      });
    }

    if (typeof alert.text === "string" && alert.text && i18n.exists(alert.text)) {
      return t(alert.text, alert.textParams || {});
    }

    return alert.text;
  }, [alert.text, alert.textKey, alert.textParams, i18n, t]);

  if (!alert.open) return null;

  const stop = (e) => e.stopPropagation();
  const tone = alert.success ? "success" : "fail";
  const progressKey = `${tone}-${durationMs}-${String(alertText)}`;

  return (
    <div className="alertOverlay" role="presentation" onClick={closeAlert}>
      <div
        className={`alertModal ${tone}`}
        role="alertdialog"
        aria-modal="true"
        onClick={stop}
      >
        <span className={`alertStatusIcon ${tone}`} aria-hidden="true" />

        <button
          className="alertCloseButton"
          onClick={closeAlert}
          aria-label={t("common.close", { defaultValue: "Close" })}
          autoFocus
        />

        <p className="alertMessage">{alertText}</p>

        {alert.autoClose && (
          <>
            <small className="alertSubText">
              {t("globalAlert.autoCloseIn", { seconds: remainSec })}
            </small>
            <div className="alertProgressTrack" aria-hidden="true">
              <span
                key={progressKey}
                className="alertProgressBar"
                style={{ "--alert-duration": `${durationMs}ms` }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
